import { UnitType } from 'utils/get-unit-by-country';

export enum TriggerType {
  BAROMETRIC_PRESSURE_HIGH = 'barometric-pressure-high',
  BAROMETRIC_PRESSURE_LOW = 'barometric-pressure-low',
  BATTERY_LOW = 'battery-low',
  BORE_HIGH = 'bore-high',
  BORE_LOW = 'bore-low',
  DAM_HIGH = 'dam-high',
  DAM_LOW = 'dam-low',
  DAM_RATE_OF_FALL = 'dam-rate-of-fall',
  DELTA_T_HIGH = 'delta-t-high',
  DELTA_T_LOW = 'delta-t-low',
  EVAPOTRANSPIRATION_AGGREGATED_HIGH = 'evapotranspiration-aggregated-high',
  FUEL_HIGH = 'fuel-high',
  FUEL_LOW = 'fuel-low',
  FUEL_RATE_OF_FALL = 'fuel-rate-of-fall',
  HEATER_POWER_SUPPLY_ISSUE = 'heater-power-supply-issue',
  HEATER_POWER_SUPPLY_RECOVERED = 'heater-power-supply-recovered',
  HEATER_WATER_TEMPERATURE_LOW = 'heater-water-temperature-low',
  HUMIDITY_HIGH = 'humidity-high',
  HUMIDITY_LOW = 'humidity-low',
  LINE_PRESSURE_HIGH = 'line-pressure-high',
  LINE_PRESSURE_LOW = 'line-pressure-low',
  LIQUID_FERTILISER_FLOW_RATE_HIGH = 'liquid-fertiliser-flow-rate-high', // only applicable to liquid fert with forecast
  LIQUID_FERTILISER_FLOW_RATE_LOW = 'liquid-fertiliser-flow-rate-low', // only applicable to liquid fert with forecast
  LIQUID_FERTILISER_LOW = 'liquid-fertiliser-low',
  LIQUID_FERTILISER_RATE_OF_FALL = 'liquid-fertiliser-rate-of-fall',
  PIPE_FLOW_RATE_HIGH = 'pipe-flow-rate-high',
  PIPE_FLOW_RATE_LOW = 'pipe-flow-rate-low',
  PIPE_VOLUME_ROLLING_WINDOW_HIGH = 'pipe-volume-rolling-window-high',
  PIPE_VOLUME_ROLLING_WINDOW_LOW = 'pipe-volume-rolling-window-low',
  POWER_METER_LINE_CURRENT_MISMATCH = 'power-meter-line-current-mismatch',
  POWER_METER_LINE_MISSING = 'power-meter-line-missing',
  POWER_METER_LINE_VOLTAGE_MISMATCH = 'power-meter-line-voltage-mismatch',
  POWER_METER_OVER_VOLTAGE = 'power-meter-over-voltage',
  POWER_METER_UNDER_VOLTAGE = 'power-meter-under-voltage',
  RAIN_ROLLING_WINDOW_HIGH = 'rain-rolling-window-high',
  SOLAR_RADIATION_HIGH = 'solar-radiation-high',
  TEMPERATURE_HIGH = 'temperature-high',
  TEMPERATURE_LOW = 'temperature-low',
  TROUGH_AVG_HIGH = 'trough-avg-high',
  TROUGH_AVG_LOW = 'trough-avg-low',
  TROUGH_HIGH = 'trough-high', // trough direct only
  TROUGH_LOW = 'trough-low', // trough direct only
  WATER_TANK_HIGH = 'watertank-high',
  WATER_TANK_LOW = 'watertank-low',
  WATER_TANK_RATE_OF_FALL = 'watertank-rate-of-fall',
  WIND_SPEED_HIGH = 'wind-speed-high'
}

export enum EventLevelColor {
  'rgb(33, 150, 243)' = 1,
  'rgb(255, 193, 7)' = 2,
  'rgb(255, 152, 0)' = 3,
  'rgb(244, 67, 54)' = 4
}

/**
 * Trigger unit type
 * BE doesn't read ValueType, so for some triggers FE set ValeType to a not
 * very reasonable value. For example, FE chooses to use LEVEL for pipe flow
 * rate triggers.
 */
export enum ValueType {
  VALUE = 1,
  RATE = 2,
  VOLUME = 3,
  REDUCED_LEVEL = 4,
  PERCENTAGE = 5
}

export enum ConditionType {
  GREATER = 1,
  GREATER_EQUALS = 2,
  LOWER = 3,
  LOWER_EQUALS = 4,
  EQUALS = 5
}

export enum EventLevel {
  INFO = 1,
  WARNING = 2,
  CRITICAL = 4
}

export enum TriggerMessage {
  AVERAGE_LEVEL_HIGH = 'Average Level High',
  AVERAGE_LEVEL_LOW = 'Average Level Low',
  BAROMETRIC_PRESSURE_HIGH = 'Barometric Pressure High',
  BAROMETRIC_PRESSURE_LOW = 'Barometric Pressure Low',
  BORE_LEVEL_HIGH = 'Bore Level High',
  BORE_LEVEL_LOW = 'Bore Level Low',
  CURRENT_LINE_MISMATCH = 'Current Line Mismatch',
  DAM_HIGH = 'Dam High',
  DAM_LOW = 'Dam Low',
  DELTA_T_HIGH = 'Delta-T High',
  DELTA_T_LOW = 'Delta-T Low',
  EVAPOTRANSPIRATION_HIGH = 'Evapotranspiration High',
  EXCESSIVE_RATE_OF_FALL = 'Rate of Excessive Fall',
  FLOW_RATE_HIGH = 'Flow Rate High',
  FLOW_RATE_LOW = 'Flow Rate Low',
  HEATER_POWER_SUPPLY_ISSUE = 'Heater Power Supply Issue',
  HEATER_POWER_SUPPLY_RECOVERED = 'Heater Power Supply Recovered',
  HEATER_WATER_TEMPERATURE_LOW = 'Heater Water Temperature Low',
  HUMIDITY_HIGH = 'Humidity High',
  HUMIDITY_LOW = 'Humidity Low',
  LINE_MISSING = 'Line Missing',
  LINE_PRESSURE_HIGH = 'Pressure Level High',
  LINE_PRESSURE_LOW = 'Pressure Level Low',
  MONITOR_BATTERY_LOW = 'Monitor Battery Low',
  OVER_VOLTAGE = 'Over Voltage',
  PIPE_VOLUME_ROLLING_WINDOW_HIGH = 'Pipe Volume High',
  PIPE_VOLUME_ROLLING_WINDOW_LOW = 'Pipe Volume Low',
  RAIN_HIGH = 'Rain High',
  SOLAR_RADIATION_HIGH = 'Solar Radiation High',
  TANK_HIGH = 'Tank Full/Overfull',
  TANK_LOW = 'Tank Low',
  TEMPERATURE_HIGH = 'Temperature High',
  TEMPERATURE_LOW = 'Temperature Low',
  TROUGH_HIGH = 'Trough High',
  TROUGH_LOW = 'Trough Low',
  UNDER_VOLTAGE = 'Under Voltage',
  VOLTAGE_LINE_MISMATCH = 'Voltage Line Mismatch',
  VOLUME_HIGH = 'Volume High',
  VOLUME_LOW = 'Volume Low',
  WIND_SPEED_HIGH = 'Wind Speed High'
}

export const TRIGGER_TYPE_TO_MESSAGE: Record<TriggerType, TriggerMessage> = {
  [TriggerType.BAROMETRIC_PRESSURE_HIGH]: TriggerMessage.BAROMETRIC_PRESSURE_HIGH,
  [TriggerType.BAROMETRIC_PRESSURE_LOW]: TriggerMessage.BAROMETRIC_PRESSURE_LOW,
  [TriggerType.BATTERY_LOW]: TriggerMessage.MONITOR_BATTERY_LOW,
  [TriggerType.BORE_HIGH]: TriggerMessage.BORE_LEVEL_HIGH,
  [TriggerType.BORE_LOW]: TriggerMessage.BORE_LEVEL_LOW,
  [TriggerType.DAM_HIGH]: TriggerMessage.DAM_HIGH,
  [TriggerType.DAM_LOW]: TriggerMessage.DAM_LOW,
  [TriggerType.DAM_RATE_OF_FALL]: TriggerMessage.EXCESSIVE_RATE_OF_FALL,
  [TriggerType.DELTA_T_HIGH]: TriggerMessage.DELTA_T_HIGH,
  [TriggerType.DELTA_T_LOW]: TriggerMessage.DELTA_T_LOW,
  [TriggerType.EVAPOTRANSPIRATION_AGGREGATED_HIGH]: TriggerMessage.EVAPOTRANSPIRATION_HIGH,
  [TriggerType.FUEL_HIGH]: TriggerMessage.TANK_HIGH,
  [TriggerType.FUEL_LOW]: TriggerMessage.TANK_LOW,
  [TriggerType.FUEL_RATE_OF_FALL]: TriggerMessage.EXCESSIVE_RATE_OF_FALL,
  [TriggerType.HEATER_POWER_SUPPLY_ISSUE]: TriggerMessage.HEATER_POWER_SUPPLY_ISSUE,
  [TriggerType.HEATER_POWER_SUPPLY_RECOVERED]: TriggerMessage.HEATER_POWER_SUPPLY_RECOVERED,
  [TriggerType.HEATER_WATER_TEMPERATURE_LOW]: TriggerMessage.HEATER_WATER_TEMPERATURE_LOW,
  [TriggerType.HUMIDITY_HIGH]: TriggerMessage.HUMIDITY_HIGH,
  [TriggerType.HUMIDITY_LOW]: TriggerMessage.HUMIDITY_LOW,
  [TriggerType.LINE_PRESSURE_HIGH]: TriggerMessage.LINE_PRESSURE_HIGH,
  [TriggerType.LINE_PRESSURE_LOW]: TriggerMessage.LINE_PRESSURE_LOW,
  [TriggerType.LIQUID_FERTILISER_FLOW_RATE_HIGH]: TriggerMessage.FLOW_RATE_HIGH,
  [TriggerType.LIQUID_FERTILISER_FLOW_RATE_LOW]: TriggerMessage.FLOW_RATE_LOW,
  [TriggerType.LIQUID_FERTILISER_LOW]: TriggerMessage.TANK_LOW,
  [TriggerType.LIQUID_FERTILISER_RATE_OF_FALL]: TriggerMessage.EXCESSIVE_RATE_OF_FALL,
  [TriggerType.PIPE_FLOW_RATE_HIGH]: TriggerMessage.FLOW_RATE_HIGH,
  [TriggerType.PIPE_FLOW_RATE_LOW]: TriggerMessage.FLOW_RATE_LOW,
  [TriggerType.PIPE_VOLUME_ROLLING_WINDOW_HIGH]: TriggerMessage.PIPE_VOLUME_ROLLING_WINDOW_HIGH,
  [TriggerType.PIPE_VOLUME_ROLLING_WINDOW_LOW]: TriggerMessage.PIPE_VOLUME_ROLLING_WINDOW_LOW,
  [TriggerType.POWER_METER_LINE_CURRENT_MISMATCH]: TriggerMessage.CURRENT_LINE_MISMATCH,
  [TriggerType.POWER_METER_LINE_MISSING]: TriggerMessage.LINE_MISSING,
  [TriggerType.POWER_METER_LINE_VOLTAGE_MISMATCH]: TriggerMessage.VOLTAGE_LINE_MISMATCH,
  [TriggerType.POWER_METER_OVER_VOLTAGE]: TriggerMessage.OVER_VOLTAGE,
  [TriggerType.POWER_METER_UNDER_VOLTAGE]: TriggerMessage.UNDER_VOLTAGE,
  [TriggerType.RAIN_ROLLING_WINDOW_HIGH]: TriggerMessage.RAIN_HIGH,
  [TriggerType.SOLAR_RADIATION_HIGH]: TriggerMessage.SOLAR_RADIATION_HIGH,
  [TriggerType.TEMPERATURE_HIGH]: TriggerMessage.TEMPERATURE_HIGH,
  [TriggerType.TEMPERATURE_LOW]: TriggerMessage.TEMPERATURE_LOW,
  [TriggerType.TROUGH_AVG_HIGH]: TriggerMessage.AVERAGE_LEVEL_HIGH,
  [TriggerType.TROUGH_AVG_LOW]: TriggerMessage.AVERAGE_LEVEL_LOW,
  [TriggerType.TROUGH_HIGH]: TriggerMessage.TROUGH_HIGH,
  [TriggerType.TROUGH_LOW]: TriggerMessage.TROUGH_LOW,
  [TriggerType.WATER_TANK_HIGH]: TriggerMessage.TANK_HIGH,
  [TriggerType.WATER_TANK_LOW]: TriggerMessage.TANK_LOW,
  [TriggerType.WATER_TANK_RATE_OF_FALL]: TriggerMessage.EXCESSIVE_RATE_OF_FALL,
  [TriggerType.WIND_SPEED_HIGH]: TriggerMessage.WIND_SPEED_HIGH
};

// Derived for reference convenience
export type PipeTriggerType =
  | TriggerType.PIPE_FLOW_RATE_HIGH
  | TriggerType.PIPE_FLOW_RATE_LOW
  | TriggerType.PIPE_VOLUME_ROLLING_WINDOW_HIGH
  | TriggerType.PIPE_VOLUME_ROLLING_WINDOW_LOW;

export type TriggerTypeWithUnit = Exclude<TriggerType,
  | TriggerType.POWER_METER_LINE_CURRENT_MISMATCH
  | TriggerType.POWER_METER_LINE_MISSING
  | TriggerType.POWER_METER_LINE_VOLTAGE_MISMATCH
  | TriggerType.POWER_METER_OVER_VOLTAGE
  | TriggerType.POWER_METER_UNDER_VOLTAGE
>;

export function assertIsTriggerTypeWithUnit(value: TriggerType): asserts value is TriggerTypeWithUnit {
  if (
    [
      TriggerType.POWER_METER_LINE_CURRENT_MISMATCH,
      TriggerType.POWER_METER_LINE_MISSING,
      TriggerType.POWER_METER_LINE_VOLTAGE_MISMATCH,
      TriggerType.POWER_METER_OVER_VOLTAGE,
      TriggerType.POWER_METER_UNDER_VOLTAGE
    ].includes(value)
  ) {
    throw new Error(`Expected to be TriggerTypeWithUnit but received ${value}`);
  }
}

export type TriggerTypeWithSingleUnitType =
  | TriggerType.BAROMETRIC_PRESSURE_HIGH
  | TriggerType.BAROMETRIC_PRESSURE_LOW
  | TriggerType.BATTERY_LOW
  | TriggerType.BORE_HIGH
  | TriggerType.BORE_LOW
  | TriggerType.DAM_RATE_OF_FALL
  | TriggerType.DELTA_T_HIGH
  | TriggerType.DELTA_T_LOW
  | TriggerType.EVAPOTRANSPIRATION_AGGREGATED_HIGH
  | TriggerType.FUEL_RATE_OF_FALL
  | TriggerType.HEATER_POWER_SUPPLY_ISSUE
  | TriggerType.HEATER_POWER_SUPPLY_RECOVERED
  | TriggerType.HEATER_WATER_TEMPERATURE_LOW
  | TriggerType.HUMIDITY_HIGH
  | TriggerType.HUMIDITY_LOW
  | TriggerType.LINE_PRESSURE_HIGH
  | TriggerType.LINE_PRESSURE_LOW
  | TriggerType.LIQUID_FERTILISER_FLOW_RATE_HIGH
  | TriggerType.LIQUID_FERTILISER_FLOW_RATE_LOW
  | TriggerType.LIQUID_FERTILISER_RATE_OF_FALL
  | TriggerType.RAIN_ROLLING_WINDOW_HIGH
  | TriggerType.SOLAR_RADIATION_HIGH
  | TriggerType.TEMPERATURE_HIGH
  | TriggerType.TEMPERATURE_LOW
  | TriggerType.TROUGH_AVG_HIGH
  | TriggerType.TROUGH_AVG_LOW
  | TriggerType.TROUGH_HIGH
  | TriggerType.TROUGH_LOW
  | TriggerType.WATER_TANK_RATE_OF_FALL
  | TriggerType.WIND_SPEED_HIGH;

export const TRIGGER_TYPE_TO_UNIT_TYPE: Record<TriggerTypeWithSingleUnitType, UnitType> = {
  [TriggerType.BAROMETRIC_PRESSURE_HIGH]: UnitType.PRESSURE_MD,
  [TriggerType.BAROMETRIC_PRESSURE_LOW]: UnitType.PRESSURE_MD,
  [TriggerType.BATTERY_LOW]: UnitType.VOLTAGE,
  [TriggerType.BORE_HIGH]: UnitType.LEVEL_MD,
  [TriggerType.BORE_LOW]: UnitType.LEVEL_MD,
  [TriggerType.DAM_RATE_OF_FALL]: UnitType.TREND_SM,
  [TriggerType.DELTA_T_HIGH]: UnitType.TEMPERATURE,
  [TriggerType.DELTA_T_LOW]: UnitType.TEMPERATURE,
  [TriggerType.EVAPOTRANSPIRATION_AGGREGATED_HIGH]: UnitType.RAIN,
  [TriggerType.FUEL_RATE_OF_FALL]: UnitType.TREND_SM,
  [TriggerType.HEATER_POWER_SUPPLY_ISSUE]: UnitType.VOLTAGE,
  [TriggerType.HEATER_POWER_SUPPLY_RECOVERED]: UnitType.VOLTAGE,
  [TriggerType.HEATER_WATER_TEMPERATURE_LOW]: UnitType.TEMPERATURE,
  [TriggerType.HUMIDITY_HIGH]: UnitType.HUMIDITY,
  [TriggerType.HUMIDITY_LOW]: UnitType.HUMIDITY,
  [TriggerType.LINE_PRESSURE_HIGH]: UnitType.PRESSURE_LG,
  [TriggerType.LINE_PRESSURE_LOW]: UnitType.PRESSURE_LG,
  [TriggerType.LIQUID_FERTILISER_FLOW_RATE_HIGH]: UnitType.VOLUME_SM,
  [TriggerType.LIQUID_FERTILISER_FLOW_RATE_LOW]: UnitType.VOLUME_SM,
  [TriggerType.LIQUID_FERTILISER_RATE_OF_FALL]: UnitType.TREND_SM,
  [TriggerType.RAIN_ROLLING_WINDOW_HIGH]: UnitType.RAIN,
  [TriggerType.SOLAR_RADIATION_HIGH]: UnitType.SOLAR_RADIATION,
  [TriggerType.TEMPERATURE_HIGH]: UnitType.TEMPERATURE,
  [TriggerType.TEMPERATURE_LOW]: UnitType.TEMPERATURE,
  [TriggerType.TROUGH_AVG_HIGH]: UnitType.LEVEL_SM,
  [TriggerType.TROUGH_AVG_LOW]: UnitType.LEVEL_SM,
  [TriggerType.TROUGH_HIGH]: UnitType.LEVEL_SM,
  [TriggerType.TROUGH_LOW]: UnitType.LEVEL_SM,
  [TriggerType.WATER_TANK_RATE_OF_FALL]: UnitType.TREND_SM,
  [TriggerType.WIND_SPEED_HIGH]: UnitType.WIND_SPEED
};